'use client';

import { useMemo, useState, FC } from 'react';
import { observer } from 'mobx-react-lite';

import { Icon, IconSizeProp, IconTypes } from '@/react/components/core-ui/Icon';
import Button from '@/react/components/core-ui/Button';
import { useLocale } from '@/react/hooks/useLocale';
import { createClickOutsideRef } from '@/react/utils/ref-utils/createClickOutsideRef';

import { getLanguageName } from '@/services/isomorphic/I18NService';
import S from './styles.base.module.scss';

/**
 * LanguageSelector component.
 *
 * Renders a language selector popover, typically used in the footer.
 * Includes:
 *   - A language popover (currently disabled and set to "English").
 */
export const LanguageSelector: FC = observer(function LanguageSelector() {
  /** Track open/closed state for the language popover. */
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  /** Current locale info (e.g., "en"). */
  const locale = useLocale();

  /** Get the name of the current language. */
  const currentLanguageCode = locale.language;
  const currentLanguageName = getLanguageName(currentLanguageCode);

  /** Toggle handler for language popover. */
  const toggleLanguage = (): void => {
    setIsLanguageOpen((prev) => !prev);
  };

  /**
   * Allows clicking outside the component to close the language popover.
   */
  const closeOnOutsideClick = useMemo(
    () =>
      createClickOutsideRef<HTMLDivElement>(() => {
        setIsLanguageOpen(false);
      }),
    [isLanguageOpen]
  );

  return (
    <div className={S.languageSelectorButton} ref={closeOnOutsideClick}>
      {/* ================= LANGUAGE SELECTOR ================= */}
      <Button
        className={S.button}
        variant="text"
        onClick={toggleLanguage}
        disabled
      >
        {currentLanguageName}
        <Icon
          className={S.arrowIcon}
          icon={IconTypes.SortUp}
          size={IconSizeProp.SizeXS}
        />
      </Button>

      {/* Language popover with available languages */}
      {isLanguageOpen && (
        <div className={S.popoverContainer}>
          {/* For now, show only the active language until more are available */}
          <div className={S.link}>{currentLanguageName}</div>
        </div>
      )}
    </div>
  );
});
